import { Box, Typography, IconButton } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import React, { useCallback, useMemo } from 'react';
import { Breadcrumb } from 'src/components/Breadcrumb';
import GridData from 'src/components/GridData/GridData';
import { TableHeader, TableHeaderItem, TABLE_HEADER_ACTION_SX } from 'src/components/TableHeader/TableHeader';
import { Button, Dropdown } from 'src/UIKit';
import TooltipCell from 'src/components/TooltipCell/TooltipCell';
import PrivateLayoutView from 'src/Layout/PrivateLayout/PrivateLayoutView';
import { getDateTimeUSAFormat } from 'src/constants/helpers';
import { Edit as EditIcon, Download as DownloadIcon, Archive as ArchiveIcon } from '@mui/icons-material';
import { EntityQRCodeParametersSimpleView } from '../../types';
import { useQRCodesViewController } from '../../controllers/QRCodesViewController';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { getCurrentCompanyName } from 'src/modules/companies/slices';
import { selectProfile } from 'src/modules/profile/slices';
import {
  getDestinationDataFromQrData,
  getDestinationTypeFromQrData,
  getQRCodeSaveParameters,
  handleDownload as handleDownloadImpl,
} from '../../helpers/helpers';
import { canArchiveQRCode } from '../../helpers/canArchiveQRCode';
import { qrCodeEditPath } from '../../helpers/qrCodeEditPath';

type QrCodeRow = EntityQRCodeParametersSimpleView;

const typeOptions = [
  { value: 'all', title: 'All' },
  { value: 'Compose SMS', title: 'Compose SMS' },
  { value: 'Go to website', title: 'Go to website' },
  { value: 'Download file', title: 'Download file' },
  { value: 'Compose Email', title: 'Compose Email' },
  { value: 'Visit Instagram', title: 'Visit Instagram' },
  { value: 'Visit Facebook', title: 'Visit Facebook' },
  { value: 'Go to Google Reviews Page', title: 'Go to Google Reviews Page' },
];

export const QRCodesView = () => {
  const navigate = useNavigate();
  const currentCompanyName = useSelector(getCurrentCompanyName);
  const { profile } = useSelector(selectProfile);
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const {
    isPageLoading,
    filter,
    list,
    changeSorting,
    changePagination,
    changeSearchFilter,
    changeTypeFilter,
    archiveQRCode,
  } = useQRCodesViewController();

  const handleCreateQRCode = () => {
    navigate('/qr-codes/create');
  };

  const handleEditQRCode = (qrCode: EntityQRCodeParametersSimpleView) => {
    navigate(qrCodeEditPath(qrCode));
  };

  const handleArchiveQRCode = (qrCode: EntityQRCodeParametersSimpleView) => {
    const recordId = qrCode.id;
    if (recordId == null) return;

    openDialog({
      title: 'Archive QR Code',
      // Not "it leaves this list": a code of an active keyword comes back as that keyword's row.
      content: `Are you sure you want to archive "${qrCode.name}"? Printed codes keep working. `
        + 'The saved styling is discarded, and a code that belongs to a keyword returns to this list unstyled.',
      confirmButtonText: 'Archive',
      onConfirm: () => {
        archiveQRCode(recordId);
      },
    });
  };

  const handleDownload = async (qrCode: EntityQRCodeParametersSimpleView) => {
    const destinationType = getDestinationTypeFromQrData(qrCode.qrcodeTypeName, qrCode.qrCodeData);
    const destinationData = getDestinationDataFromQrData(qrCode.qrCodeData, destinationType);
    const formData = {
      ...getQRCodeSaveParameters(qrCode.parameters),
      destinationData,
    };

    // The stored data, not one guessed back from the form: a type the builder cannot represent
    // would be downloaded as another type's code.
    await handleDownloadImpl(formData, destinationType, qrCode.name, currentCompanyName, qrCode.qrCodeData);
  };

  const columns: (GridColDef<QrCodeRow> & { initVisibility?: boolean })[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        renderCell: (params: GridRenderCellParams<QrCodeRow>) => {
          return (
            <Box display="flex" alignItems="center" height="100%" width="100%">
              <TooltipCell value={params.row.name} variant="body2" maxWidth="100%" />
            </Box>
          );
        },
        minWidth: 240,
        editable: false,
        sortable: true,
        flex: 2,
      },
      {
        field: 'qrcodeTypeName',
        headerName: 'Type',
        renderCell: (params: GridRenderCellParams<QrCodeRow>) => {
          return (
            <Box display="flex" alignItems="center" height="100%" width="100%">
              <TooltipCell value={params.row.qrcodeTypeName} variant="body2" maxWidth="100%" />
            </Box>
          );
        },
        minWidth: 160,
        editable: false,
        sortable: true,
        flex: 1,
      },
      {
        field: 'dateCreated',
        headerName: 'Created At',
        renderCell: (params: GridRenderCellParams<QrCodeRow>) => {
          return (
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="body2">
                {getDateTimeUSAFormat(new Date(params.row.dateCreated))}
              </Typography>
            </Box>
          );
        },
        minWidth: 170,
        editable: false,
        sortable: true,
        flex: 1,
      },
      {
        field: 'dateUpdated',
        headerName: 'Updated At',
        renderCell: (params: GridRenderCellParams<QrCodeRow>) => {
          return (
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="body2">
                {getDateTimeUSAFormat(new Date(params.row.dateUpdated))}
              </Typography>
            </Box>
          );
        },
        minWidth: 170,
        editable: false,
        sortable: true,
        flex: 1,
      },
    ],
    [],
  );

  const rows: QrCodeRow[] = useMemo(() => list?.items || [], [list?.items]);

  // A code generated from a keyword has no record id, and the grid still needs one row key per row.
  const rowId = useCallback((row: QrCodeRow) => row.id ?? `keyword:${row.keywordId}`, []);

  return (
    <>
      <PrivateLayoutView
        title={
          <Breadcrumb items={[{ label: 'QR Codes', isActive: true }]} />
        }
      >
        <GridData
          getRowId={rowId}
          header={
            <TableHeader
              searchValue={filter.query}
              onSearchChange={changeSearchFilter}
              filters={
                <TableHeaderItem>
                  <Dropdown
                    label="Type"
                    value={filter.type || 'all'}
                    options={typeOptions}
                    onChange={(_, value) => changeTypeFilter(value === 'all' ? '' : value)}
                    margin="none"
                    size="small"
                  />
                </TableHeaderItem>
              }
              actions={
                <Button
                  variant="contained"
                  onClick={handleCreateQRCode}
                  sx={TABLE_HEADER_ACTION_SX}
                >
                  Create QR Code
                </Button>
              }
            />
          }
          isLoading={isPageLoading}
          rows={rows}
          columns={columns}
          sorting={{
            value: filter.sort,
            onChange: changeSorting,
          }}
          pagination={{
            page: filter.page || 0,
            pageSize: filter.limit || 25,
            totalCount: list?.count,
            onChange: changePagination,
          }}
          renderAction={r => {
            return (
              <>
                <IconButton size="small" color="primary" onClick={() => handleEditQRCode(r)}>
                  <EditIcon fontSize="inherit" />
                </IconButton>
                <IconButton size="small" color="primary" onClick={() => handleDownload(r)}>
                  <DownloadIcon fontSize="inherit" />
                </IconButton>
                {canArchiveQRCode(r, profile) && (
                  <IconButton
                    size="small"
                    color="primary"
                    title="Archive"
                    aria-label={`Archive ${r.name}`}
                    onClick={() => handleArchiveQRCode(r)}
                  >
                    <ArchiveIcon fontSize="inherit" />
                  </IconButton>
                )}
              </>
            );
          }}
        />
      </PrivateLayoutView>
      <ConfirmationDialog />
    </>
  );
};

export default QRCodesView;
